import { Column, Row } from "@/shared/ui/Blocks";
import React, { useState } from "react";
import { IconButton } from "@/shared/ui/IconButton";
import { InputField } from "@/shared/ui/InputField";
import { Icon, IconName } from "@/shared/ui/Icon";
import { DropdownMenu } from "../DropdownMenu";
import { cn } from "@/shared/utils/shadcn";
import { useToast } from "@/shared/contexts/toast.context";

interface ContactsRowProps {
  email?: string;
  phone?: string;
  linkedin?: string;
  isContact?: boolean;
  onContactChange?: ({
    email,
    phone,
    linkedin,
  }: {
    email?: string;
    phone?: string;
    linkedin?: string;
  }) => void;
  editable?: boolean;
}

interface ContactItemProps {
  type: "email" | "phone" | "linkedin";
  value?: string;
  onChange?: (value?: string) => void;
  placeholder?: string;
  editable?: boolean;
}

const ContactIcon: Record<ContactItemProps["type"], IconName> = {
  email: "Email",
  phone: "Phone",
  linkedin: "LinkedIn",
};

const parseContactValue = (type: ContactItemProps["type"], value?: string) => {
  if (type === "linkedin") {
    const baseUrl = "https://www.linkedin.com/in/";
    if (value?.startsWith(baseUrl)) {
      return value?.replace(baseUrl, "");
    }
  }
  return value;
};

const ContactItem = ({
  type,
  value,
  onChange,
  placeholder,
  editable = true,
}: ContactItemProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const { showToast } = useToast();

  const onChangeEditMode = (enabled: boolean) => {
    setIsEditMode(enabled);
  };

  const handleValueSubmit = (value: string) => {
    onChange?.(editValue || "");
    setIsEditMode(false);
  };

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    showToast({
      message: "Copied to clipboard",
      type: "success",
    });
  };

  if (isEditMode) {
    return (
      <Row className="w-full items-center gap-2">
        <InputField
          onChange={(e) => setEditValue(e.target.value)}
          autoFocus
          size="small"
          defaultValue={value}
          className="gap-2 py-0"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleValueSubmit(editValue || "");
            }
          }}
        />
        <IconButton
          icon="Check"
          size="sm"
          className="px-0 aspect-square"
          onClick={() => handleValueSubmit(editValue || "")}
        />
      </Row>
    );
  }

  if (!value && editable) {
    return (
      <IconButton
        variant="ghost"
        color="blue"
        icon="Plus"
        onClick={() => onChangeEditMode(true)}
        label={placeholder}
        size="sm"
        className="text-blue-500 hover:text-blue-500/80 hover:bg-transparent print:hidden"
      />
    );
  }

  if (!value) return null;

  return (
    <Row className="items-center gap-4 justify-between group w-full relative">
      <Row
        className="items-center gap-2 cursor-pointer"
        onClick={() => handleCopy(editValue || value)}
      >
        <Icon size="sm" icon={ContactIcon[type]} />
        <span className="truncate max-w-[250px] overflow-hidden">
          {parseContactValue(type, editValue || value)}
        </span>
      </Row>
      <Column>
        {editable && (
          <DropdownMenu
            align="end"
            items={[
              {
                label: "Edit",
                icon: "Edit",
                onClick: () => onChangeEditMode(true),
                hidden: !editable || isEditMode,
              },
            ]}
            trigger={
              <IconButton
                icon="KebabVertical"
                size="sm"
                variant="ghost"
                color="black"
              />
            }
          />
        )}
      </Column>
    </Row>
  );
};

export const ContactsRow: React.FC<ContactsRowProps> = (props) => {
  const {
    email,
    phone,
    linkedin,
    onContactChange,
    editable = true,
    isContact = false,
  } = props;
  const handleContactChange =
    (type: ContactItemProps["type"]) => (value?: string) => {
      onContactChange?.({ [type]: value });
    };

  const dataItems = [
    {
      type: "email",
      value: email,
      placeholder: "Add email address",
      editable: isContact && editable, // If the person is not a contact, the email is not editable
    },
    {
      type: "phone",
      value: phone,
      placeholder: "Add phone number",
      editable,
    },
    {
      type: "linkedin",
      value: linkedin,
      placeholder: "Add LinkedIn profile",
      editable,
    },
  ] as const;

  return (
    <Row className="justify-between pt-4 flex-wrap">
      {dataItems.map((item) => (
        <Row
          key={`${item.type}-wrapper`}
          className={cn(
            "flex-1 border-l-1 border-gray-300 border-solid rounded-none first:border-l-0 pl-2 first:pl-0",
            !item.value && !editable && "print:border-none"
          )}
        >
          <ContactItem
            key={item.type}
            type={item.type}
            value={item.value}
            onChange={handleContactChange(item.type)}
            placeholder={item.placeholder}
            editable={item.editable}
          />
        </Row>
      ))}
    </Row>
  );
};
